import {LoadingService,} from '@pages/loading/loading.service';
import {BehaviorSubject, NEVER, Observable, catchError, finalize, switchMap, tap,} from 'rxjs';
import {Injectable,} from '@angular/core';
import {registerLocaleData,} from '@angular/common';
import {Router,} from '@angular/router';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import localeJa from '@angular/common/locales/ja';
import {
  SystemConstant,
  Logger,
  LoginInfoService,
  MessageStackService,
  ScreenMessageHandler,
  StringUtil,
  WindowService,
  MessageProvider,
} from '@services/index';
import {LoginUserVO, MessageLevel, MessageVO,} from '@models/index';
import {MatDialog,} from '@angular/material/dialog';

registerLocaleData(localeJa);

/**
 * リクエストのインターセプター.
 */
@Injectable()
export class RequestInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  constructor(private router: Router,
              private loginInfoService: LoginInfoService,
              private windowService: WindowService,
              private logger: Logger,
              private screenMessage: ScreenMessageHandler,
              private constant: SystemConstant,
              private loadingService: LoadingService,
              private stringUtil: StringUtil,
              private dialog: MatDialog,
              private messageProvider: MessageProvider) { }

  private keiyakuInfoId!: string;

  /**
   * [一般開発者利用不可]
   * APIコールをインターセプトして、URLを完成させたり、リクエストヘッダーに認証情報を付与する。
   * また、レスポンスを判定し、正常でなければエラーハンドリングを行う。
   * HTTPStatusによってはTokenのリフレッシュなども行う。
   * @param request original request
   * @param next next
   */
  intercept<T>(request: HttpRequest<T>, next: HttpHandler): Observable<T> | Observable<HttpEvent<T>> {

    // セッション変数が取得出来た場合、契約情報IDを取得
    const connVo: LoginUserVO = this.loginInfoService.getLoginUserInfo();
    if (connVo && connVo.hashId != null && !this.stringUtil.isEmpty(connVo.hashId)
        && connVo.accessToken  != null && !this.stringUtil.isEmpty(connVo.accessToken)
        && connVo.refreshToken != null && !this.stringUtil.isEmpty(connVo.refreshToken)) {

      this.keiyakuInfoId = connVo.keiyakuInfoId.toString();
    } else {
      this.keiyakuInfoId = '';
    }

    this.logger.debug(this.keiyakuInfoId + ' ■ -- start-- intercept');

    //トークンリフレッシュ処理中か判断するフラグ
    let isTokenRefFlg = false;
    //トークンリフレッシュ処理中ではない場合、ローディング表示を開始する
    if(!request.url.includes('/auth/refresh')){
      this.loadingService.startAllApi();
    }
    // URLがhttpやhttpsから始まる場合は、外部APIコールに相当するため、interceptしない
    if(request.url.startsWith('http:') || request.url.startsWith('https:')){
      return next.handle(request);
    // URLがhttpやhttpsから始まらない場合は、内部APIコールに相当するため、interceptを行う
    } else {
      const apiUrl = this.constant.SERVER_HOST + this.constant.API_PATH + request.url;
      const authorizedReq = this.addAuthorizationHeader(request.clone({url: apiUrl,}));

      // async パラメータが存在する場合は、非同期トランザクションフラグをオンにする
      const async = request.params.get('async');
      if (async) {
        this.windowService.asyncTransactionFlag = true;
      }

      // リクエストを送信
      return next.handle(authorizedReq).pipe(
        finalize(() => {
          //トークンリフレッシュ処理中ではないかつ非同期処理ではない場合、ローディング表示を終了する
          if(!request.url.includes('/auth/refresh') && !isTokenRefFlg && !this.windowService.asyncTransactionFlag){
            this.loadingService.stopAllApi(true);
          }
        }),
        tap(resp => {
          if (resp instanceof HttpResponse) {
            if (resp.body) {
              const uui = resp.body.updateUserInfo;
              if (uui) {
                this.loginInfoService.setLoginUserInfo(uui);
              }
            }
            this.convertDateStringsToDates(resp.body);
          }
          return resp;
        }),
        catchError((error: HttpErrorResponse) => {

          // トークンリフレッシュ処理時のエラーの場合、refreshTokenメソッドのエラーハンドリングに渡す
          if (error.url?.includes('/auth/refresh')) {
            throw error;
          }
          // ローディング表示を終了する
          if (this.windowService.asyncTransactionFlag === true) {
            this.windowService.asyncTransactionFlag = false;
          }
          this.loadingService.stopAllApi();

          // HTTPコード
          this.logger.info('SMS99901M001I', '', 1, error.status, error.message);
          if (error.status !== 200) {
            const messageStack: MessageStackService = new MessageStackService(MessageLevel.ERROR);

            if (error.status === 401) {
              this.logger.error('SMS99901M004E');
              messageStack.add({messageId: 'SMS99901M004E',});

            } else if (error.status === 400) {
              if (error.error && typeof (error.error as MessageVO[]) !== 'undefined') {
                this.convertDateStringsToDates(<object><unknown>(error.error as MessageVO[])[0]);
              }
              this.logger.errorWithFreeText('APIコール時にHTTP STATUS:400のエラーが発生');
              if(error.error as MessageVO[]) {

                // ICSと同じようにaddAllで設定すると、画面側のcatchでlength=0になって拾えない
                // ⇒ 業務処理的に、エラーメッセージは1件しか戻らないので、配列[0]をAddする処理追加

                this.logger.debug(this.keiyakuInfoId + ' ■ -- sms-server-app：エラーメッセージ有り');
                if (error.error.length === 1) {
                  messageStack.add(error.error[0] as MessageVO);
                } else {
                  messageStack.addAll(error.error as MessageVO[]);
                }

              } else {
                messageStack.add({messageId: 'SMS99901M013E',});
              }
            } else if (error.status === 403) {
              isTokenRefFlg = true;
              this.logger.debug('[1]AccessTokenの有効期限が切れている場合はAccessTokenのRefreshを行う');

              // AccessTokenの有効期限が切れている場合はAccessTokenのRefreshを行う
              // return this.refreshToken(request, next);

              messageStack.add({messageId: 'SMS99901M004E',});

            } else if (error.status === 500) {
              this.logger.errorWithFreeText('APIコール時にサーバーサイドでHTTP STATUS:500のエラーが発生');
              messageStack.add({messageId: 'SMS99901M013E',});
            } else if (error.status === 502 || error.status === 503 || error.status === 504) {
              this.logger.errorWithFreeText('APIコール時にサーバーサイドでHTTP STATUS:502,503,もしくは504のエラーが発生');
              messageStack.add({messageId: 'SMS99901M013E',});
            } else if (error.error) {
              this.logger.errorWithFreeText('APIコール時にサーバーサイドでエラーが発生');
              messageStack.add({messageId: 'SMS99901M013E',});
            } else {
              this.logger.errorWithFreeText('API呼び出し処理において対応するアクションがありません');
              messageStack.add({messageId: 'SMS99901M013E',});
            }

            this.logger.debug(this.keiyakuInfoId + ' [1] throw messageStack');
            throw messageStack;
          }
          return NEVER; // 後続の処理は実行しない。
        })
      );
    }
  }

  /**
   * [一般開発者利用不可]
   * ヘッダーに認証トークンを付与する。
   * @param req HTTPRequestオブジェクト
   * @private
   */
  private addAuthorizationHeader<T>(req: HttpRequest<T>): HttpRequest<T> {
    const accToken = this.loginInfoService.getAccessToken();
    const refToken = this.loginInfoService.getRefreshToken();
    if (accToken && refToken) {
      return req.clone({
        headers: req.headers.append('Authorization', 'Bearer ' + accToken)
          .append('AuthorizationRefresh', refToken).append('timeout', '900000'),
        withCredentials: true,
      });
    } else {
      return req;
    }
  }

  /**
   * [一般開発者利用不可]
   * AccessTokenリフレッシュ処理
   * @param request オリジナルのHTTPRequest
   * @param next next
   * @private
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private refreshToken<T>(request: HttpRequest<T>, next: HttpHandler): Observable<any> {

    this.logger.debug(this.keiyakuInfoId + ' ■ -- start-- refreshToken');

    if (!this.isRefreshing) {
      this.logger.info('CSS01999M030I');
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);
      return this.loginInfoService.refreshAccessToken().pipe(
        finalize(() => this.isRefreshing = false),
        switchMap((user: LoginUserVO) => {
          this.refreshTokenSubject.next(user.accessToken);
          const apiUrl = this.constant.SERVER_HOST + this.constant.API_PATH + request.url;
          return next.handle(this.addAuthorizationHeader(request.clone({url: apiUrl,}))).pipe(
            finalize(() => {
              //非同期処理場合、ローディング表示を終了する
              if(!this.windowService.asyncTransactionFlag){
                //ローディング表示を終了する
                this.loadingService.stopAllApi();
              }
            }),
            tap(event => {
              if (event instanceof HttpResponse) {
                if (event.body) {
                  const uui = event.body.updateUserInfo;
                  if (uui) {
                    this.loginInfoService.setLoginUserInfo(uui);
                  }
                }
                this.convertDateStringsToDates(event.body);
              }
              return event;
            }),
            catchError((error: HttpErrorResponse) => {

              // ローディング表示を終了する
              this.loadingService.stopAllApi();

              // HTTPコード
              this.logger.info('SMS99901M001I', '', 2, error.status, error.message);
              if (error.status !== 200) {
                const messageStack: MessageStackService = new MessageStackService(MessageLevel.ERROR);

                if (error.status === 401) {
                  this.logger.error('SMS99901M004E');
                  messageStack.add({messageId: 'SMS99901M004E',});
                } else if (error.status === 400) {
                  if (error.error && typeof (error.error as MessageVO[]) !== 'undefined') {
                    this.convertDateStringsToDates(<object><unknown>(error.error as MessageVO[])[0]);
                  }
                  this.logger.errorWithFreeText('APIコール時にHTTP STATUS:400のエラーが発生');
                  messageStack.add({messageId: 'SMS99901M013E',});
                } else if (error.status === 500) {
                  this.logger.errorWithFreeText('APIコール時にサーバーサイドでHTTP STATUS:500のエラーが発生');
                  messageStack.add({messageId: 'SMS99901M013E',});
                } else if (error.status === 502 || error.status === 503 || error.status === 504) {
                  this.logger.errorWithFreeText('APIコール時にサーバーサイドでHTTP STATUS:502,503,もしくは504のエラーが発生');
                  messageStack.add({messageId: 'SMS99901M013E',});
                } else if (error.error) {
                  this.logger.errorWithFreeText('APIコール時にサーバーサイドでエラーが発生');
                  messageStack.add({messageId: 'SMS99901M013E',});
                } else {
                  this.logger.errorWithFreeText('API呼び出し処理において対応するアクションがありません');
                  messageStack.add({messageId: 'SMS99901M013E',});
                }

                this.logger.debug(this.keiyakuInfoId + ' [2] throw messageStack');
                throw messageStack;
              }
              return NEVER; // 後続の処理は実行しない。
            })
          );
        }),
        catchError((error: MessageStackService | HttpErrorResponse) => {
          // ローディング表示を終了する
          this.loadingService.stopAllApi();

          // トークンリフレッシュ処理のエラーでない場合、エラー内容を画面に渡す
          if (error instanceof MessageStackService ||
            (error.url && !error.url?.includes('/auth/refresh'))) {

            this.logger.debug(this.keiyakuInfoId + ' [3] throw error');
            throw error;
          }

          // HTTPコード
          this.logger.info('SMS99901M001I', '', 3, error.status, error.message);
          if (error.status !== 200) {

            if (error.status === 400 || error.status === 403) {
              this.logger.errorWithFreeText('Tokenリフレッシュの際にサーバーサイドでHTTP STATUS:400もしくは403のエラーが発生');
            } else if (error.status === 401) {
              this.logger.error('SMS99901M004E');
            } else if (error.status === 502 || error.status === 503 || error.status === 504) {
              this.logger.errorWithFreeText('Tokenリフレッシュの際にサーバーサイドでHTTP STATUS:502,503もしくは504のエラーが発生');
            } else if (error.error) {
              this.logger.errorWithFreeText('Tokenリフレッシュの際にサーバーサイドでエラーが発生');
            } else {
              this.logger.errorWithFreeText('API呼び出し処理において対応するアクションがありません');
            }

            const messageStack: MessageStackService = new MessageStackService(MessageLevel.ERROR);
            //messageStack.add({messageId: 'SMS99901M013E',});

            // ★Memo
            // AccessTokenリフレッシュでエラー発生した場合、
            // 画面表示するのは「認証情報の有効期限が切れました」の方が正しい
            messageStack.add({messageId: 'SMS99901M004E',});

            this.logger.debug(this.keiyakuInfoId + ' [3] throw messageStack');
            throw messageStack;
          }
          return NEVER; // 後続の処理は実行しない。
        })
      );
    } else {
      // TokenRefreshが2重で発生したためスルーする旨をログに出力
      this.logger.error('CSS01999M031E');
      return NEVER; // 後続の処理は実行しない。
    }
  }

  /**
   * [一般開発者利用不可]
   * LocalDateTimeAdapter経由のナノ秒までの日付フォーマットの文字列で楽観的排他制御に使われない項目だけDateオブジェクトに変換する
   * LocalDateAdapter経由の日付の文字列もDateオブジェクトに変換する
   * @private
   */
  private convertDateStringsToDates(input: object): void {
    const ISO_8601 =
      /^[0-9]{4}[-/][0-9]{1,2}[-/][0-9]{1,2}[T ][0-9]{1,2}:[0-9]{1,2}:[0-9]{1,2}(\.[0-9]+)?(([+-][0-9]{1,2}:[0-9]{1,2})|Z)?$/;
    for (const key in input) {
      if (key === null) {
        continue;
      }
      const value = input[key];
      const match = String(value).match(ISO_8601);

      // LocalDateTimeAdapter経由のナノ秒までの日付フォーマットの文字列で楽観的排他制御に使われない項目だけDateオブジェクトに変換する
      // またLocalDateAdapter経由の日付の文字列もDateオブジェクトに変換する
      if (typeof value === 'string' && match !== null) {
        const dateVal: string = match[0].replace(/\//g, '-').replace(' ', 'T') + '+09:00';
        const milliseconds = Date.parse(dateVal);
        if (!isNaN(milliseconds)) {
          input[key] = new Date(dateVal);
        }
      } else if (typeof value === 'object') {
        this.convertDateStringsToDates(<object>value);
      }
    }
  }

}
